import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "api-reference"
    }}>{`API Reference`}</h1>
    <p>{`Discoverability via TypeScript types is a design goal for `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{`. Nevertheless, this page aims to describe all user-facing APIs and how you might use them.`}</p>
    <p>{`The API for `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` comes in 3 parts:`}</p>
    <ol>
      <li parentName="ol"><inlineCode parentName="li">{`<AuthConfig />`}</inlineCode>{` to configure auth`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`useAuth()`}</inlineCode>{` to interact with the auth system`}</li>
      <li parentName="ol"><inlineCode parentName="li">{`dispatch()`}</inlineCode>{` for direct access to the underlying XState machine`}</li>
    </ol>
    <h2 {...{
      "id": "authconfig"
    }}>{`AuthConfig`}</h2>
    <p><inlineCode parentName="p">{`<AuthConfig />`}</inlineCode>{` is a component that helps you configure `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{`. You don't need to use AuthConfig but it makes life easier.`}</p>
    <p><inlineCode parentName="p">{`<AuthConfig />`}</inlineCode>{` accepts 3 props:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`authProvider`}</inlineCode>{`, an auth provider class conforming to the `}<a parentName="li" {...{
          "href": "/docs/auth-providers"
        }}>{`auth providers`}</a>{` spec`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`navigate`}</inlineCode>{`, a routing function for your environment`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`params`}</inlineCode>{`, optional params object to configure your auth provider. Each auth provider defines its own API for params`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`children`}</inlineCode>{`, optional React children if you prefer wrapping. But AuthConfig is not a context provider.`}</li>
    </ul>
    <p>{`The component ensures your auth provider is initialized and sessions are checked on render. You can do this yourself with the `}<a parentName="p" {...{
        "href": "/docs/api-reference#dispatch"
      }}>{`dispatch() API`}</a>{`.`}</p>
    <h2 {...{
      "id": "useauth"
    }}>{`useAuth()`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`useAuth()`}</inlineCode>{` hook is the main API you use to interact with this library.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const Component = () => {
    const { ... } = useAuth()

    // do stuff

    return <RenderStuff />
}
`}</code></pre>
    <p>{`The hook exposes these values:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`isAuthenticating`}</inlineCode>{`, boolean whether we're currently authenticating. Great for loading spinners`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`isAuthenticated()`}</inlineCode>{`, method to check if user is currently authenticated`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`isAuthorized()`}</inlineCode>{`, method to check if user has the right roles or scopes`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`user`}</inlineCode>{`, object with user properties; differs between providers`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`userId`}</inlineCode>{`, string identifying the current user`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`authResult`}</inlineCode>{`, object with the latest authentication result`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`login()`}</inlineCode>{`, function to trigger login process`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`signup()`}</inlineCode>{`, function to trigger signup process`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`logout()`}</inlineCode>{`, function to trigger logout process`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`handleAuthentication()`}</inlineCode>{`, function to use in auth callback pages`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`dispatch()`}</inlineCode>{`, function to access the underlying XState machinery`}</li>
    </ul>
    <p>{`If your editor supports TypeScript and JSDoc hinting, you should see a list of these APIs every time you use `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "dispatch"
    }}>{`dispatch()`}</h2>
    <p><a parentName="p" {...{
        "href": "https://useauth.dev"
      }}><inlineCode parentName="a">{`useAuth`}</inlineCode></a>{` uses `}<a parentName="p" {...{
        "href": "https://xstate.js.org/"
      }}>{`XState`}</a>{` to manage state.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/31d79/xstate-machine.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "34.08071748878923%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'136\\'%20viewBox=\\'0%200%20400%20136\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M63%2021c-5%200-7%202-6%204%201%201%201%201%201-1%201-2%202-2%2013-3a477%20477%200%2001-8%200M9%2027l3%204%202%204c-1%204%202%204%2024%204%2024%200%2024%200%2024-5%200-3%202-4%204-1%202%202%2018%202%2019-1l17-1h17l-17-1-17-1c-1-3-18-3-19%200l-2%201-2-1c0-2-3-2-23-2s-24%200-24%202c-1%202-3%201-3-1l-1-3-2%202m181%202l-3%201-2%201h2l3%201c2%203%2018%203%2020%201l3-2-1-1-2-2c-2-2-18-1-20%201M16%2033v4h45v-9H16v5m8-2c-6%200-7%200-7%202l21%201%2021-1c0-2-1-2-9-2a158%20158%200%2000-16%200l-1-1-9%201m104%200c-3%200-4%201-4%202s2%202%2012%202l12%201h1c1-2%208-3%209-1s3%200%203-2-1-2-4-2a942%20942%200%2001-29%200m81%205c-18%200-20%201-20%204s3%204%2021%204c16%200%2018%200%2019-2%202-1%202-2%201-3v-2c1-1-4-1-21-1M66%2038l-2%202%202%202c3%203%2038%203%2041%200v-4c-3-3-37-3-41%200m84%201l-4%201c-7%200-8%200-8%202l7%201%207%201h1l2-1%203-1h1l2%201%203-1h1l7%201%206-1h1c1%203%204%202%204%200s0-2-19-2l-13-1h-1m3%208c0%204-2%206-4%204l-2-1h-6c-4%200-6%201-6%202%200%202%205%203%209%202h15l16-1%202%201c1%202%204%200%203-2-1-1-3-2-13-2-11%201-12%200-13-3-1-2-1-2-1%200m-88%201l-1%202%202%201a128%20128%200%200036%201l-1-3v-2l-17-1c-15%200-17%200-19%202m125-1l-3%202-2%201%202%201%203%201c3%202%2033%201%2034-1v-3c-2-3-33-3-34-1m-37%2024v4h-11l-13-1c-2-3-45-3-46%200h-4a186%20186%200%20000%2027l1-15v-1c-2-2-1-9%201-9l2%202%2023%201%2023-1c1-2%204-2%2013-2h11v3l1%203v-3c0-3%200-3%204-3h5l-6-1c-3%200-3%200-3-4l-1-3v3m94%201l-8%202h-4a226%20226%200%20000%2039c0-7%201-9%204-5l20%201%2020-1%203-2%201-1h-1l-3-1c-2-3-38-3-40%200l-3%201c-2%200-2-19%200-19v-2c-2%200-1-8%200-8l3%202c2%202%2021%202%2023%200l2-2v-1l-2-1c1-2-5-3-7-3l-8%201m-83%2024v24h71l-35-1h-35V73h35l35-1h-71v24m203-6v18h-36l-36%201h73V73l-1%2017M171%2076c-3%200-5%202-5%203l18%201c17%200%2018-1%2018-3s-1-3-2-1h-29m-87%207c-2%202-2%202%200%204%201%202%203%202%2032%202s31%200%2033-2v-4c-2-2-4-2-33-2s-31%200-32%202m156%200l-2%202%202%202c2%203%2032%202%2033%200v-4c-1-2-31-3-33%200M33%2085c-1%200-2%201-1%202%200%201%208%202%208%200h12l2%201%203-1h1c0%202%2013%202%2013%200h1c1%203%204%202%204%200%200-1-1-2-3-2a160%20160%200%2000-28%200H33m160%208c1%203%202%203%2015%203h20l1-1v-2c1-2-26-2-29%200h-1c0-2-1-2-3-1h-1l-1-1c-1%200-2%201-1%202m120-1l-2%202c1%202%207%202%2010%201h28c16%200%2016%200%2016-2l-1-1h-37c-1-1-2-1-2%201h-1c0-2%200-2-2-1h-9M30%2096c-4%201%203%204%208%203h5l5-1h1l2%201%203-1h1c0%202%2013%202%2013%200h1c1%203%204%202%204%200s-1-2-3-2H30m305%203h-5c-3%200-4%201-5%202h-1c0-2-12-2-13%200%200%202%201%202%205%202l6-1h1l28%201c2%200%204-1%203-3l-7-1h-12m-157%204l-1%203h50l5-1c0-2-1-2-8-2h-30c-1-1-2%200-2%201h-1c0-1%200-2-1-1h-12m-129%203h-8c-4%200-6%201-7%202%200%202%203%203%204%201h30c0-2-1-2-8-2l-9-1h-2m154%204l-6%201-5%201h-1c0-2-13-2-13%200-1%202%200%202%2018%202h19c2%202%206%200%206-2l-8-2h-10m-101%2022\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
        `}<source parentName="picture" {...{
                "srcSet": ["/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/ca0a1/xstate-machine.webp 223w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/75680/xstate-machine.webp 445w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/8d1ba/xstate-machine.webp 890w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/3838e/xstate-machine.webp 1335w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/e11e5/xstate-machine.webp 1780w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/11261/xstate-machine.webp 1954w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
        `}<source parentName="picture" {...{
                "srcSet": ["/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/e92b6/xstate-machine.png 223w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/e66bf/xstate-machine.png 445w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/4ef49/xstate-machine.png 890w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/4e814/xstate-machine.png 1335w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/701e9/xstate-machine.png 1780w", "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/31d79/xstate-machine.png 1954w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
        `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/9c78e2b29d3f4dde83e5d3b9eb5d0ceb/4ef49/xstate-machine.png",
                "alt": "useAuth XState machine",
                "title": "useAuth XState machine",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
      `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`useAuth XState machine`}</figcaption>{`
  `}</figure></p>
    <p>{`You can use the exposed `}<inlineCode parentName="p">{`dispatch()`}</inlineCode>{` method to trigger actions manually. `}<inlineCode parentName="p">{`<AuthConfig>`}</inlineCode>{` uses this to handle configuration.`}</p>
    <p>{`Run this code in any React component to get access:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const { dispatch } = useAuth();

// dispatch('ACTION')
// dispatch('ACTION', { payload })
`}</code></pre>
    <p>{`You can then fire any of these actions:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`LOGIN`}</inlineCode>{` starts the login process`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`CHECK_SESSION`}</inlineCode>{` verifies the current user session`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`SET_CONFIG`}</inlineCode>{` configures useAuth`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`ERROR`}</inlineCode>{` moves the machine into an error end-state`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`AUTHENTICATED`}</inlineCode>{` sets user data`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`LOGOUT`}</inlineCode>{` clears user data`}</li>
    </ul>
    <p>{`Using `}<inlineCode parentName="p">{`dispatch()`}</inlineCode>{` manually is discouraged. The most common use-case is for configuring `}<inlineCode parentName="p">{`useAuth`}</inlineCode>{` without the `}<inlineCode parentName="p">{`<AuthConfig>`}</inlineCode>{` component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// how AuthConfig uses dispatch()

export const AuthConfig = ({
    authProvider,
    params,
    navigate,
    children
}) => {
    const { dispatch } = useAuth();

    const callbackDomain =
        typeof window !== "undefined"
            ? \`\${window.location.protocol}//\${window.location.host}\`
            : "http://localhost:8000";

    React.useEffect(() => {
        // instantiate auth provider on page load
        const authInstance = new authProvider({
            dispatch,
            ...authProvider.addDefaultParams(
                params as ProviderOptions,
                callbackDomain
            )
        });

        // set config in XState
        dispatch("SET_CONFIG", {
            authProvider: authInstance,
            navigate,
            callbackDomain
        });

        dispatch("CHECK_SESSION");
    }, [dispatch, authProvider, params, navigate]);

    return <>{children}</>;
};
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      